import { Injectable, inject } from '@angular/core';
import * as qs from "qs";
import {firstValueFrom, throwError} from "rxjs";
import {ResponseModel, SingleResponseModel} from "../../../shared/response.model";
import {environment} from "../../../../environments/environment";
import {LoadingState} from "../../../shared/loading-state.enum";
import {ContactType} from "../comment/comment.model";
import { HttpClient } from "@angular/common/http";
import {AuthService} from "../../../login/auth.service";
import {SocialSupportNote} from "./social-support.model";
import {Attachment} from "../attachments/attachment.model";
import * as FileSaver from "file-saver";

@Injectable({
  providedIn: 'root'
})
export class SocialSupportService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  socialSupportNotes: SocialSupportNote[] = [];
  socialSupportAttachments: Attachment[] = [];
  loadingState = LoadingState.Loading;

  async fetchSocialSupportNotes(customerId: number): Promise<void> {
    const query = qs.stringify({
      filters: {
        customer: {
          id: {
            $eq: customerId
          }
        }
      },
      populate: {
        users_permissions_user: true,
        contact_type: true
      }
    }, {encodeValuesOnly: true});
    try {
      const response = await firstValueFrom(this.http.get<ResponseModel<SocialSupportNote>>(environment.apiUrl + `/social-support-notes?${query}`));
      this.socialSupportNotes = response.data;
      this.loadingState = LoadingState.Data;
    } catch (e) {
      console.error(e);
      this.loadingState = LoadingState.Error;
      this.socialSupportNotes = [];
    }
  }

  async createSocialSupportNote(contactType: ContactType, text: string, customerId: number): Promise<SocialSupportNote> {
    const response = await firstValueFrom(this.http.post<SingleResponseModel<SocialSupportNote>>(environment.apiUrl + "/social-support-notes", {
      data: {
        contact_type: contactType.id,
        text,
        customer: customerId,
        users_permissions_user: this.authService.user()?.id
      }
    }));
    return response?.data;
  }

  async deleteSocialSupportNote(id: string): Promise<SocialSupportNote> {
    const response = await firstValueFrom(this.http.delete<SingleResponseModel<SocialSupportNote>>(environment.apiUrl + `/social-support-notes/${id}`));
    return response?.data;
  }

  async fetchAttachments(customerId: number): Promise<void> {
    const query = qs.stringify({
      filters: {
        customer: {
          id: {
            $eq: customerId
          }
        }
      },
      sort: 'createdAt:desc',
      populate: ['file']
    }, { encodeValuesOnly: true });
    try {
      const response = await firstValueFrom(this.http.get<ResponseModel<Attachment>>(`${environment.apiUrl}/social-support-attachments?${query}`));
      this.socialSupportAttachments = response.data;
      this.loadingState = LoadingState.Data;
    } catch (e) {
      this.loadingState = LoadingState.Error;
      console.error(e);
      this.socialSupportAttachments = [];
    }
  }

  async createAttachment(customerId: number, name: string, file: File): Promise<void> {
    const formData = new FormData();

    formData.append('files.file', file, file.name);
    formData.append('data', JSON.stringify({
      name,
      customer: customerId
    }));

    await firstValueFrom(this.http.post<SingleResponseModel<Attachment>>(`${environment.apiUrl}/social-support-attachments`, formData));
  }

  async deleteAttachment(id: number): Promise<void> {
    await firstValueFrom(this.http.delete<SingleResponseModel<Attachment>>(`${environment.apiUrl}/social-support-attachments/${id}`));
  }

  async downloadAttachment(attachment: Attachment) {
    this.http.get(`${environment.apiUrl}/social-support-attachments/${attachment.id}/download`, { responseType: 'blob' }).subscribe(
      (response) => {

        const blob = new Blob([response]);
        FileSaver.saveAs(blob, attachment.file.name);
      },
      e => { throwError(e); }
    );
  }
}
